import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Lightbulb, Shield, TrendingUp, Users, Award } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: <Lightbulb size={32} />,
      title: 'Innovation',
      description: 'Technology-driven solutions designed for modern mobility.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Reliability',
      description: 'Safe, durable, and professionally installed charging systems.'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Scalability',
      description: 'Solutions that grow from single-home chargers to multi-site commercial deployments.'
    },
    {
      icon: <Target size={32} />,
      title: 'Sustainability',
      description: 'Supporting cleaner transport and smarter energy use.'
    },
    {
      icon: <Users size={32} />,
      title: 'Customer Support',
      description: 'Practical guidance and ongoing technical assistance.'
    }
  ];

  return (
    <div className="min-h-screen">
      <section
        data-testid="about-hero-section"
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(10, 61, 46, 0.85), rgba(10, 61, 46, 0.7)), url('https://static.prod-images.emergentagent.com/jobs/fc6e7ca9-8e07-44ba-abc5-d4f04b264f75/images/f8b82b57dd82596c574f37c25187cc2e164fe9b0616ca300e2bde624a1ebeb2a.png')`
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter text-white mb-6 font-['Outfit']">
              About Green Circuit EV
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Accelerating South Africa's transition to electric mobility through reliable, intelligent charging solutions.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="about-story-section" className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-[#4A5D54] leading-relaxed mb-6">
                Green Circuit EV is a South African electric vehicle charging solutions company focused on making electric mobility accessible, reliable, and future-ready.
              </p>
              <p className="text-lg text-[#4A5D54] leading-relaxed mb-6">
                What began with smart residential charging solutions has evolved into a broader mission: helping South Africa build the infrastructure needed for the next generation of transportation.
              </p>
              <p className="text-lg text-[#4A5D54] leading-relaxed mb-6">
                Today, we supply and install EV charging solutions for homes, businesses, commercial properties, and public environments.
              </p>
              <p className="text-lg text-[#4A5D54] leading-relaxed">
                Our solutions are designed with South African conditions in mind — balancing safety, scalability, energy efficiency, and ease of use.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="mission-vision-section" className="py-24 bg-[#F4F7F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-12 rounded-2xl border border-[#E2E8F0]"
            >
              <div className="w-16 h-16 bg-[#0A3D2E] rounded-2xl flex items-center justify-center mb-6">
                <Target size={32} className="text-[#CDFF00]" />
              </div>
              <h2 className="text-3xl font-semibold text-[#0A3D2E] mb-4 font-['Outfit']">Mission</h2>
              <p className="text-base text-[#4A5D54] leading-relaxed">
                To accelerate South Africa's transition to clean and accessible electric mobility through reliable, intelligent charging solutions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-12 rounded-2xl border border-[#E2E8F0]"
            >
              <div className="w-16 h-16 bg-[#0A3D2E] rounded-2xl flex items-center justify-center mb-6">
                <Eye size={32} className="text-[#CDFF00]" />
              </div>
              <h2 className="text-3xl font-semibold text-[#0A3D2E] mb-4 font-['Outfit']">Vision</h2>
              <p className="text-base text-[#4A5D54] leading-relaxed">
                To become one of South Africa's leading EV infrastructure providers by delivering innovative charging solutions that support the future of transport.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="values-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-4 font-['Outfit']">
              Core Values
            </h2>
            <p className="text-base text-[#4A5D54] max-w-2xl mx-auto">
              The principles that guide everything we do.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl border border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="w-16 h-16 bg-[#E9F0EC] rounded-2xl flex items-center justify-center mb-6 text-[#0A3D2E]">
                  {value.icon}
                </div>
                <h3 className="text-xl font-medium text-[#0A3D2E] mb-3 font-['Outfit']">{value.title}</h3>
                <p className="text-base text-[#4A5D54]">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="commitment-section" className="py-24 bg-[#0A3D2E] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 bg-[#CDFF00] rounded-full flex items-center justify-center mx-auto mb-8">
              <Award size={40} className="text-[#0A3D2E]" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6 font-['Outfit']">
              Our Commitment
            </h2>
            <p className="text-lg text-white/90 leading-relaxed max-w-3xl mx-auto">
              We're committed to providing South Africa with world-class EV charging infrastructure that's safe, reliable, and accessible. Every installation is backed by professional service, ongoing support, and a dedication to building a cleaner energy future.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
